import * as Yup from 'yup';

import { Icon } from 'components/icon';
import { Company, ButtonColors } from 'types';
import { getUploadedFile } from 'utils/helpers';
import { MyCompanyInfo } from 'store/api/company';
import {
  phoneNumber,
  getValidations,
  getMappedObject,
  validateZipCode,
} from 'utils/validation';

import { FormValues } from './types';

export const otherFields = ['fax', 'photo', 'website'];

export const booleanFields = [
  'isTipsEnabled',
  'isProcessingFeeEnabled',
  'isApprovalRequestEnabled',
  'isEventCompetitionEnabled',
  'isTipFeeEnabled',
  'isBrokenMissingEnabled',
];

export const requiredFields = [
  'zip',
  'city',
  'name',
  'email',
  'phone',
  'state',
  'address',
  'businessOwner',
  'processingFee',
  'isProcessingFeeEnabled',
  'isApprovalRequestEnabled',
  'isEventCompetitionEnabled',
];

export const getInitialValues = (
  data?: Company | MyCompanyInfo
): FormValues => ({
  ...getMappedObject<FormValues>(booleanFields, false),
  ...getMappedObject<FormValues>([...otherFields, ...requiredFields], ''),
  ...data,
  fax: data?.fax ?? '',
  website: data?.website ?? '',
  address: data?.address ?? '',
  tipOptions: data?.tipOptions ?? [],
  isTipFeeEnabled: data?.isTipFeeEnabled ?? false,
  notificationEmails: data?.notificationEmails ?? [''],
  notificationPhones: data?.notificationPhones ?? [''],
  processingFee: data?.processingFee?.toString() ?? '',
  tipFeePercent: formatPayrollPercent(data?.tipFeePercent, 6),
  isBrokenMissingEnabled: data?.isBrokenMissingEnabled ?? false,
  isApprovalRequestEnabled: data?.isApprovalRequestEnabled ?? false,
  photo: data?.photoUrl ? getUploadedFile(data.photoUrl) : undefined,
  isEventCompetitionEnabled: data?.isEventCompetitionEnabled ?? false,
  brokenMissingPercent: formatPayrollPercent(data?.brokenMissingPercent, 1),
});

/** "6.00" -> "6", "1.50" -> "1.5"; the default when unset. */
export function formatPayrollPercent(
  value: number | string | undefined,
  fallback: number
) {
  const number = Number(value);
  return String(
    value === undefined || value === null || value === '' || isNaN(number)
      ? fallback
      : number
  );
}

const payrollPercent = Yup.string()
  .required('Enter a percentage')
  .test(
    'payroll-percent',
    'Enter 0 to 100, at most two decimals',
    (value) =>
      !!value &&
      /^\d{1,3}(\.\d{1,2})?$/.test(value.trim()) &&
      Number(value) >= 0 &&
      Number(value) <= 100
  );

export const validationSchema = Yup.object().shape({
  ...getValidations(requiredFields),
  ...getMappedObject(otherFields, Yup.string()),
  ...getMappedObject(booleanFields, Yup.boolean()),
  photo: Yup.object(),
  zip: validateZipCode(),
  tipFeePercent: payrollPercent,
  brokenMissingPercent: payrollPercent,
  email: Yup.string().email().required(),
  processingFee: Yup.number().positive().max(5).required(),
  phone: phoneNumber('Phone number is invalid').required(),
  notificationPhones: Yup.array().of(phoneNumber('Phone number is invalid')),
  notificationEmails: Yup.array().of(
    Yup.string().email('Value must be a valid email')
  ),
  tipOptions: Yup.array().of(
    Yup.number()
      .typeError('Each tip must be a number')
      .min(1, 'Tip values must be at least 1')
      .max(100, 'Tip values cannot exceed 100')
  ),
  isTipsEnabled: Yup.boolean().test(
    'tips-options-check',
    'When tips are enabled, you must provide unique tip options',
    function (value) {
      if (!value) {
        return true;
      }
      const opts: number[] = this.parent.tipOptions ?? [];

      if (opts.length < 1) {
        return false;
      }

      const uniqueCount = new Set(opts).size;
      if (uniqueCount !== opts.length) {
        return false;
      }
      return true;
    }
  ),
});

export const commonButtonProps = {
  padding: '0',
  transparent: true,
  icon: <Icon.Edit />,
  colorType: ButtonColors.LightBlue,
};
